import React, { useState, useEffect, useCallback } from 'react';
import { applicants } from '../api';

/**
 * Shows whether an applicant attached a resume, and lets a user download it.
 *
 * The file is fetched through the authenticated API (not a public URL), and
 * the server sends it with Content-Disposition: attachment, so it always
 * saves to disk rather than rendering in the browser.
 *
 * Deliberately not included in the exported applicant PDF.
 */
function ApplicantResume({ applicantId, applicantName }) {
  const [meta, setMeta] = useState(null);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);
  const [error, setError] = useState('');

  const loadMeta = useCallback(async () => {
    if (!applicantId) return;
    setLoading(true);
    setError('');
    try {
      const response = await applicants.getResumeMeta(applicantId);
      setMeta(response.data);
    } catch (err) {
      setError('Could not check for a resume.');
    } finally {
      setLoading(false);
    }
  }, [applicantId]);

  useEffect(() => {
    loadMeta();
  }, [loadMeta]);

  const handleDownload = async () => {
    if (downloading) return;
    setDownloading(true);
    setError('');
    try {
      const response = await applicants.downloadResume(applicantId);
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', meta?.filename || 'resume');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setError('Could not download the resume.');
    } finally {
      setDownloading(false);
    }
  };

  const formatSize = (bytes) => {
    if (!bytes) return '';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  return (
    <div className="review-section">
      <h3 style={{ margin: '0 0 12px 0' }}>Resume / CV</h3>

      {error && (
        <div style={{ color: '#b91c1c', marginBottom: '12px', fontSize: '14px' }}>
          {error}
        </div>
      )}

      {loading ? (
        <div style={{ color: '#94a3b8', fontSize: '14px' }}>Checking...</div>
      ) : !meta?.has_resume ? (
        <div style={{ padding: '12px', background: '#f8fafc', borderRadius: '6px' }}>
          <em style={{ color: '#94a3b8' }}>
            {applicantName ? `${applicantName} did not attach a resume.` : 'No resume attached.'}
          </em>
        </div>
      ) : (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: '12px',
            padding: '12px',
            background: '#f8fafc',
            borderRadius: '6px'
          }}
        >
          <div style={{ minWidth: 0 }}>
            <div style={{ fontSize: '14px', fontWeight: 600, overflowWrap: 'anywhere' }}>
              {meta.filename}
            </div>
            <div style={{ fontSize: '12px', color: '#94a3b8' }}>
              {formatSize(meta.byte_size)}
              {meta.uploaded_at
                ? ` · uploaded ${new Date(meta.uploaded_at).toLocaleDateString()}`
                : ''}
            </div>
          </div>
          <button
            className="btn btn-primary"
            onClick={handleDownload}
            disabled={downloading}
            style={{ whiteSpace: 'nowrap' }}
          >
            {downloading ? 'Downloading...' : '⬇ Download'}
          </button>
        </div>
      )}
    </div>
  );
}

export default ApplicantResume;
